import { Download16 } from '@carbon/icons-react';
import { Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FileAttachment } from 'api/attachments';
import { CpBody00Primary } from 'components/atoms/customer-portal/typography';
import { Loading } from 'components/atoms/loading';
import { FileExtensionIcon } from 'components/organisms/files/file-extension-icon';
import { extname, isImageUrlFromExtension } from 'utils/files';
import { getRandomColor } from 'utils/get-random-color';
import { queueMacrotask } from 'utils/macrotask';
import { SrcIconWrapper } from '../space/spaces-grid-view';
import { CpFilesEmptyState } from './cp-all-files-empty-state';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 174px);
  gap: var(--cp-spacing-05);
  justify-content: start;
`;

const GridItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px solid var(--cp-gray-20);
  border-radius: var(--border-radius-sm);
  width: 174px;
  height: 174px;
  cursor: pointer;
`;

// const FileType = styled.div<{ color: string }>`
//   background: ${(props) => `var(--cp-${props.color}-50)`};
//   width: 100%;
//   height: 100%;
//   display: flex;
//   align-items: end;
//   justify-content: end;
//   padding-bottom: 0;
//   padding-right: var(--cp-spacing-06);
// `;

const FileNameWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;
  padding-top: var(--cp-spacing-05);
  padding-bottom: var(--cp-spacing-05);
  padding-left: var(--cp-spacing-06);
  padding-right: var(--cp-spacing-06);
  overflow: hidden;
`;

const FileName = styled(CpBody00Primary)`
  margin-bottom: 0;
  /* ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  flex: 1 1 auto;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  min-width: 32px;
  cursor: pointer;
  border-radius: var(--border-radius-base);

  visibility: hidden;

  ${GridItem}:hover & {
    visibility: visible;
  }

  &:hover {
    background: var(--cp-gray-10);
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: var(--cp-gray-10);

  .folded-file-svg {
    position: absolute;
    bottom: -50px;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
  }
  .rl-document-svg {
    fill: var(--cp-blue-50);
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Item: React.FC<{
  attachment: FileAttachment;
  onDownload?: (attachment: FileAttachment) => void;
  onFileClick: () => void;
}> = ({ attachment, onDownload, onFileClick }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const ext = useMemo(() => extname(attachment?.name), [attachment?.name]);

  return (
    <GridItem onClick={onFileClick}>
      {isImageUrlFromExtension(attachment?.name) ? (
        <ImageWrapper>
          <Image
            src={attachment?.location?.replaceAll(
              'rocket-aws',
              'cp-with-spotlight.orbits.run',
            )}
            alt={attachment?.name}
          />
        </ImageWrapper>
      ) : (
        <ImageWrapper color={getRandomColor(ext)}>
          <SrcIconWrapper>
            <FileExtensionIcon name={attachment?.name} height={32} width={32} />
          </SrcIconWrapper>
        </ImageWrapper>
      )}
      <FileNameWrapper>
        <Tooltip title={attachment.name}>
          <FileName>{attachment.name}</FileName>
        </Tooltip>
        <Tooltip title={<FormattedMessage id="download" />}>
          <IconWrapper
            onClick={(event) => {
              event.stopPropagation();
              setIsDownloading(true);
              queueMacrotask(() => {
                onDownload?.(attachment);
              });
              queueMacrotask(() => {
                setIsDownloading(false);
              });
            }}
          >
            {isDownloading ? <Loading /> : <Download16 />}
          </IconWrapper>
        </Tooltip>
      </FileNameWrapper>
    </GridItem>
  );
};

export const CpFilesGridView: React.FC<{
  attachments: FileAttachment[];
  onDownload?: (attachment: FileAttachment) => void;
  onFileClick: (attachment: FileAttachment) => void;
}> = ({ attachments, onDownload, onFileClick }) => {
  return attachments?.length ? (
    <Grid>
      {attachments.map((attachment) => {
        return (
          <Item
            key={attachment.attachmentId}
            attachment={attachment}
            onDownload={onDownload}
            onFileClick={() => onFileClick(attachment)}
          />
        );
      })}
    </Grid>
  ) : (
    <CpFilesEmptyState />
  );
};
